import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, faBell, faFont, faDatabase, faLock, 
  faCircleQuestion, faHeadphones, faChevronRight,
  faHouse, faBook, faChartSimple, faGear,
  faMinus, faPlus, faVolumeHigh, faCheck
} from '@fortawesome/free-solid-svg-icons';

// 假设我们有一个全局设置的服务
import { useSettings } from '../hooks/useSettings';
import { getAvailableVoiceTypes } from '../utils/speechService';

const Settings = () => {
  const navigate = useNavigate();
  const { settings, updateSettings, availableVoices, previewVoice } = useSettings();
  
  // 本地状态，用于UI交互
  const [voiceSpeed, setVoiceSpeed] = useState(settings.voiceSpeed || 3);
  const [repeatCount, setRepeatCount] = useState(settings.repeatCount || 3);
  const [repeatInterval, setRepeatInterval] = useState(settings.repeatInterval || 3);
  const [wordInterval, setWordInterval] = useState(settings.wordInterval || 5);
  const [fontSize, setFontSize] = useState(settings.fontSize || '中');
  const [voiceType, setVoiceType] = useState(settings.voiceType || 'system');
  const [voiceId, setVoiceId] = useState(settings.voiceId || '');
  
  // 模态框状态
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState(null); // 'voiceType', 'voice'
  
  // 获取可用的语音类型
  const voiceTypes = getAvailableVoiceTypes();
  
  // 当本地状态变化时，更新全局设置
  useEffect(() => {
    updateSettings({
      voiceSpeed,
      repeatCount,
      repeatInterval,
      wordInterval,
      fontSize,
      voiceType,
      voiceId
    });
  }, [voiceSpeed, repeatCount, repeatInterval, wordInterval, fontSize, voiceType, voiceId, updateSettings]);
  
  // 处理语音速度变化
  const handleVoiceSpeedChange = (e) => {
    setVoiceSpeed(parseInt(e.target.value));
  };
  
  // 处理重复次数变化
  const handleRepeatCountChange = (increment) => {
    const newValue = repeatCount + increment;
    if (newValue >= 1 && newValue <= 5) {
      setRepeatCount(newValue);
    }
  };
  
  // 处理重复间隔时间变化
  const handleRepeatIntervalChange = (increment) => {
    const newValue = repeatInterval + increment;
    if (newValue >= 1 && newValue <= 10) {
      setRepeatInterval(newValue);
    }
  };
  
  // 处理单词间隔时间变化
  const handleWordIntervalChange = (increment) => {
    const newValue = wordInterval + increment;
    if (newValue >= 1 && newValue <= 10) {
      setWordInterval(newValue);
    }
  };
  
  // 获取语音速度文本
  const getVoiceSpeedText = () => {
    const speedTexts = ['很慢', '慢', '正常', '快', '很快'];
    return speedTexts[voiceSpeed - 1] || '正常';
  };
  
  // 打开模态框
  const openModal = (type) => {
    setModalType(type);
    setModalOpen(true);
  };
  
  // 关闭模态框
  const closeModal = () => {
    setModalOpen(false);
    setModalType(null);
  };
  
  // 选择语音类型
  const selectVoiceType = (type) => {
    setVoiceType(type);
    setVoiceId(''); // 重置声音选择
    closeModal();
  };
  
  // 选择声音
  const selectVoice = (id) => {
    setVoiceId(id);
    closeModal();
  };
  
  // 获取当前选择的语音类型名称
  const getVoiceTypeName = () => {
    const selectedType = voiceTypes.find(type => type.id === voiceType);
    return selectedType ? selectedType.name : '系统语音';
  };
  
  // 获取当前选择的声音名称
  const getVoiceName = () => {
    if (!voiceId || !availableVoices[voiceType]) return '默认';
    
    debugger;
    const selectedVoice = availableVoices[voiceType].find(voice => voice.id === voiceId);
    return selectedVoice ? selectedVoice.name : '默认';
  };
  
  // 试听当前语音
  const handlePreviewVoice = () => {
    const cancelFn = previewVoice('Hello, this is a test');
    
    // 5秒后自动取消播放
    setTimeout(() => {
      if (cancelFn && typeof cancelFn.cancel === 'function') {
        cancelFn.cancel();
      }
    }, 5000);
  };
  
  // 导航到其他页面
  const navigateTo = (path) => {
    navigate(path);
  };
  
  return (
    <div className="bg-[#f9f9f9] min-h-screen">
      {/* Main Content */}
      <div className="p-5 pb-24">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold">设置</h2>
        </div>

        {/* General Settings */}
        <div className="mb-6 hidden">
          <h3 className="font-bold text-lg mb-3">基本设置</h3>
          <div className="bg-white rounded-xl shadow-sm">
            <div 
              className="p-4 border-b border-gray-100 flex justify-between items-center cursor-pointer"
              onClick={() => navigateTo('/profile')}
            >
              <div className="flex items-center">
                <FontAwesomeIcon icon={faUser} className="text-blue-500 mr-3 text-lg" />
                <div className="font-medium">个人信息</div>
              </div>
              <FontAwesomeIcon icon={faChevronRight} className="text-gray-400 text-xs" />
            </div>
            <div 
              className="p-4 border-b border-gray-100 flex justify-between items-center cursor-pointer"
              onClick={() => navigateTo('/notifications')}
            >
              <div className="flex items-center">
                <FontAwesomeIcon icon={faBell} className="text-blue-500 mr-3 text-lg" />
                <div className="font-medium">通知</div>
              </div>
              <FontAwesomeIcon icon={faChevronRight} className="text-gray-400 text-xs" />
            </div>
            <div 
              className="p-4 flex justify-between items-center cursor-pointer"
              onClick={() => navigateTo('/font-size')}
            >
              <div className="flex items-center">
                <FontAwesomeIcon icon={faFont} className="text-blue-500 mr-3 text-lg" />
                <div className="font-medium">字体大小</div>
              </div>
              <div className="flex items-center">
                <span className="text-gray-500 mr-2">{fontSize}</span>
                <FontAwesomeIcon icon={faChevronRight} className="text-gray-400 text-xs" />
              </div>
            </div>
          </div>
        </div>

        {/* Dictation Settings */}
        <div className="mb-6">
          <h3 className="font-bold text-lg mb-3">听写设置</h3>
          <div className="bg-white rounded-xl shadow-sm">
            {/* 语音类型选择 */}
            <div 
              className="p-4 border-b border-gray-100 flex justify-between items-center cursor-pointer"
              onClick={() => openModal('voiceType')}
            >
              <div className="flex items-center">
                <div className="font-medium">语音类型</div>
              </div>
              <div className="flex items-center">
                <span className="text-gray-500 mr-2">{getVoiceTypeName()}</span>
                <FontAwesomeIcon icon={faChevronRight} className="text-gray-400 text-xs" />
              </div>
            </div>
            
            {/* 声音选择 */}
            <div 
              className="p-4 border-b border-gray-100 flex justify-between items-center cursor-pointer"
              onClick={() => openModal('voice')}
            >
              <div className="flex items-center">
                <div className="font-medium">声音选择</div>
              </div>
              <div className="flex items-center">
                <span className="text-gray-500 mr-2">{getVoiceName()}</span>
                <FontAwesomeIcon icon={faChevronRight} className="text-gray-400 text-xs" />
              </div>
            </div>
            
            {/* 试听按钮 */}
            <div className="p-4 border-b border-gray-100 flex justify-between items-center">
              <div className="flex items-center">
                <div className="font-medium">试听当前语音</div>
              </div>
              <button 
                className="bg-blue-500 text-white px-4 py-2 rounded-full text-sm flex items-center"
                onClick={handlePreviewVoice}
              >
                <FontAwesomeIcon icon={faVolumeHigh} className="mr-2" />
                试听
              </button>
            </div>
            
            <div className="p-4 border-b border-gray-100 hidden">
              <div className="flex justify-between items-center mb-2">
                <div className="font-medium">语音速度</div>
                <div className="text-blue-500">{getVoiceSpeedText()}</div>
              </div>
              <div className="flex items-center justify-between px-2">
                <span className="text-xs text-gray-500">慢</span>
                <div className="flex-1 mx-3">
                  <input 
                    type="range" 
                    min="1" 
                    max="5" 
                    value={voiceSpeed} 
                    onChange={handleVoiceSpeedChange}
                    className="w-full"
                  />
                </div>
                <span className="text-xs text-gray-500">快</span>
              </div>
            </div>
            <div className="p-4 border-b border-gray-100 flex justify-between items-center">
              <div className="flex items-center">
                <div className="font-medium">单词重复次数</div>
              </div>
              <div className="flex items-center space-x-3">
                <button 
                  className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
                  onClick={() => handleRepeatCountChange(-1)}
                >
                  <FontAwesomeIcon icon={faMinus} className="text-gray-500 text-xs" />
                </button>
                <span className="w-8 text-center font-medium">{repeatCount}</span>
                <button 
                  className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
                  onClick={() => handleRepeatCountChange(1)}
                >
                  <FontAwesomeIcon icon={faPlus} className="text-gray-500 text-xs" />
                </button>
              </div>
            </div>
            <div className="p-4 border-b border-gray-100 flex justify-between items-center">
              <div className="flex items-center">
                <div className="font-medium">重复间隔时间</div>
              </div>
              <div className="flex items-center space-x-3">
                <button 
                  className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
                  onClick={() => handleRepeatIntervalChange(-1)}
                >
                  <FontAwesomeIcon icon={faMinus} className="text-gray-500 text-xs" />
                </button>
                <span className="w-8 text-center font-medium">{repeatInterval}</span>
                <button 
                  className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
                  onClick={() => handleRepeatIntervalChange(1)}
                >
                  <FontAwesomeIcon icon={faPlus} className="text-gray-500 text-xs" />
                </button>
              </div>
            </div>
            <div className="p-4 flex justify-between items-center">
              <div className="flex items-center">
                <div className="font-medium">单词间隔时间</div>
              </div>
              <div className="flex items-center space-x-3">
                <button 
                  className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
                  onClick={() => handleWordIntervalChange(-1)}
                >
                  <FontAwesomeIcon icon={faMinus} className="text-gray-500 text-xs" />
                </button>
                <span className="w-8 text-center font-medium">{wordInterval}</span>
                <button 
                  className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
                  onClick={() => handleWordIntervalChange(1)}
                >
                  <FontAwesomeIcon icon={faPlus} className="text-gray-500 text-xs" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Advanced Settings */}
        <div className="mb-6 hidden">
          <h3 className="font-bold text-lg mb-3">高级设置</h3>
          <div className="bg-white rounded-xl shadow-sm">
            <div 
              className="p-4 border-b border-gray-100 flex justify-between items-center cursor-pointer"
              onClick={() => navigateTo('/data-management')}
            >
              <div className="flex items-center">
                <FontAwesomeIcon icon={faDatabase} className="text-blue-500 mr-3 text-lg" />
                <div className="font-medium">数据管理</div>
              </div>
              <FontAwesomeIcon icon={faChevronRight} className="text-gray-400 text-xs" />
            </div>
            <div 
              className="p-4 border-b border-gray-100 flex justify-between items-center cursor-pointer"
              onClick={() => navigateTo('/privacy')}
            >
              <div className="flex items-center">
                <FontAwesomeIcon icon={faLock} className="text-blue-500 mr-3 text-lg" />
                <div className="font-medium">隐私设置</div>
              </div>
              <FontAwesomeIcon icon={faChevronRight} className="text-gray-400 text-xs" />
            </div>
            <div 
              className="p-4 flex justify-between items-center cursor-pointer"
              onClick={() => navigateTo('/help')}
            >
              <div className="flex items-center">
                <FontAwesomeIcon icon={faCircleQuestion} className="text-blue-500 mr-3 text-lg" />
                <div className="font-medium">帮助与反馈</div>
              </div>
              <FontAwesomeIcon icon={faChevronRight} className="text-gray-400 text-xs" />
            </div>
          </div>
        </div>

        {/* App Info */}
        <div className="bg-white rounded-xl p-5 shadow-sm text-center">
          <FontAwesomeIcon icon={faHeadphones} className="text-blue-500 text-xl mb-2" />
          <h3 className="font-bold">爱上听写</h3>
          <p className="text-gray-500 text-sm">版本 1.0.0</p>
          <p className="text-xs text-gray-400 mt-2">© 2025 爱上听写团队</p>
        </div>
      </div>
      
      {/* 模态框 */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-4/5 max-h-[70vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-bold text-lg text-center">
                {modalType === 'voiceType' ? '选择语音类型' : '选择声音'}
              </h3>
            </div>
            
            <div className="p-2">
              {modalType === 'voiceType' && (
                <div>
                  {voiceTypes.map(type => (
                    <div 
                      key={type.id}
                      className="p-3 flex justify-between items-center border-b border-gray-100 cursor-pointer"
                      onClick={() => selectVoiceType(type.id)}
                    >
                      <span>{type.name}</span>
                      {voiceType === type.id && (
                        <FontAwesomeIcon icon={faCheck} className="text-blue-500" />
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {modalType === 'voice' && (
                <div>
                  <div 
                    className="p-3 flex justify-between items-center border-b border-gray-100 cursor-pointer"
                    onClick={() => selectVoice('')}
                  >
                    <span>默认</span>
                    {voiceId === '' && (
                      <FontAwesomeIcon icon={faCheck} className="text-blue-500" />
                    )}
                  </div>
                  
                  {availableVoices[voiceType] && availableVoices[voiceType].map(voice => (
                    <div 
                      key={voice.id}
                      className="p-3 flex justify-between items-center border-b border-gray-100 cursor-pointer"
                      onClick={() => selectVoice(voice.id)}
                    >
                      <span>{voice.name}</span>
                      {voiceId === voice.id && (
                        <FontAwesomeIcon icon={faCheck} className="text-blue-500" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="p-4 flex justify-center">
              <button 
                className="bg-blue-500 text-white px-6 py-2 rounded-full"
                onClick={closeModal}
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;